import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Loader2, AlertTriangle, Video } from 'lucide-react';
import { Button } from '../components/ui/button';
import MeetingRoom from '../components/MeetingRoom';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * Public client landing page.
 *
 * URL:  /join/:roomId?t=<guest_token>
 *
 * No login, no account, no app download. Shows a one-click "Join meeting"
 * button (browsers block auto-getUserMedia without user gesture, so the
 * button doubles as the gesture trigger). Once clicked, MeetingRoom mounts
 * in role="guest".
 */
const PublicJoinPage = () => {
  const { roomId } = useParams();
  const [params] = useSearchParams();
  const guestToken = params.get('t');

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joined, setJoined] = useState(false);
  const [name, setName] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!guestToken) {
        setError('This link is missing or invalid. Please ask your broker for a new link.');
        setLoading(false);
        return;
      }
      try {
        const res = await fetch(`${BACKEND}/api/public/meetings/${roomId}/info`);
        if (res.status === 410) throw new Error('This meeting has ended or expired.');
        if (!res.ok) throw new Error('Meeting not found.');
        const data = await res.json();
        if (cancelled) return;
        setMeeting(data);
        if (data.client_name) setName(data.client_name);
      } catch (e) {
        if (!cancelled) setError(e.message || 'Could not load meeting');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [roomId, guestToken]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white">
        <Loader2 className="h-8 w-8 animate-spin text-emerald-500" />
      </div>
    );
  }
  if (error || !meeting) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-950 text-white p-6">
        <div className="text-center max-w-sm">
          <AlertTriangle className="h-10 w-10 mx-auto text-rose-500 mb-3" />
          <h2 className="text-xl font-semibold mb-1">Can't join this meeting</h2>
          <p className="text-slate-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!joined) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-950 via-slate-900 to-emerald-950 text-white flex items-center justify-center p-6" data-testid="public-join-landing">
        <div className="max-w-md w-full">
          <div className="bg-slate-900/70 backdrop-blur border border-white/10 rounded-2xl p-8 shadow-2xl text-center">
            <div className="w-16 h-16 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
              <Video className="h-8 w-8 text-emerald-400" />
            </div>
            <h1 className="text-2xl font-bold mb-1">{meeting.title || 'Live consultation'}</h1>
            <p className="text-sm text-slate-400 mb-6">
              {meeting.host_name ? `${meeting.host_name} is ready to meet you.` : 'You\'ve been invited to a live meeting.'}
            </p>

            <div className="text-left mb-5">
              <label className="text-xs uppercase tracking-wide text-slate-400 font-semibold mb-1 block">Your name</label>
              <input
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full bg-slate-800 border border-white/10 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-emerald-500"
                placeholder="Enter your name"
                data-testid="guest-name-input"
              />
            </div>

            <Button
              onClick={() => setJoined(true)}
              className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-6 text-base"
              data-testid="join-meeting-btn"
            >
              <Video className="h-5 w-5 mr-2" /> Join Meeting
            </Button>

            <p className="text-[11px] text-slate-500 mt-4">
              You'll be asked to allow camera & microphone access.<br />
              No download, no account — your browser is all you need.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <MeetingRoom
      roomId={meeting.room_id}
      token={guestToken}
      role="guest"
      hostName={meeting.host_name}
      clientName={name || meeting.client_name}
      meetingId={meeting.id}
      onLeave={() => { window.location.href = '/'; }}
    />
  );
};

export default PublicJoinPage;
